import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  Flex,
  Layout,
  Menu,
  Progress,
  Row,
  Segmented,
  Space,
  Statistic,
  Spin,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd'
import type { MenuProps, TableProps } from 'antd'
import {
  AdminApiError,
  AdminMenuItem,
  AdminOrder,
  getMe,
  hasCsrfToken,
  listActiveOrders,
  listAdminMenu,
  logout,
  OrderStatus,
  Staff,
  setMenuAvailability,
  transitionOrder,
} from './api'
import { formatFen } from './format'
import LoginPage from './LoginPage'

const { Header, Sider, Content } = Layout
const { Title, Text } = Typography

const menuLabels: Record<string, string> = {
  dashboard: '工作台',
  orders: '订单中心',
  menu: '菜单管理',
  store: '门店管理',
  marketing: '营销中心',
  members: '会员服务',
  reports: '数据中心',
  settings: '系统设置',
}

const menuItems: MenuProps['items'] = Object.entries(menuLabels).map(([key, label]) => ({ key, label }))

function OrderCard({
  order,
  onAdvance,
  loading,
}: {
  order: AdminOrder
  onAdvance: (order: AdminOrder, target: OrderStatus) => void
  loading: boolean
}) {
  const target: OrderStatus = order.status === 'PAID'
    ? 'PREPARING'
    : order.status === 'PREPARING' && order.fulfillmentType === 'PICKUP'
      ? 'READY'
      : 'COMPLETED'
  const action = order.status === 'PAID' ? '接单并制作' : order.status === 'PREPARING' ? '完成制作' : '确认取餐'
  const elapsedMinutes = Math.max(0, Math.floor((Date.now() - new Date(order.createdAt).getTime()) / 60_000))
  const platform = { WECHAT: '微信', DOUYIN: '抖音', ALIPAY: '支付宝' }[order.platform]
  const fulfillment = order.fulfillmentType === 'PICKUP' ? '自取' : '堂食'
  return (
    <Card className="order-card" size="small">
      <Flex justify="space-between" align="flex-start">
        <Space direction="vertical" size={0}>
          <Text className="pickup-number">{order.pickupNumber ?? order.tableNumber}</Text>
          <Text type="secondary">{fulfillment} · {platform}</Text>
        </Space>
        <Tag color={elapsedMinutes > 10 ? 'error' : 'orange'}>{elapsedMinutes} 分钟</Tag>
      </Flex>
      <div className="order-items">{order.lines.map((line) => <div key={line.menuItemId}>{line.name} ×{line.quantity}</div>)}</div>
      {order.remark && <div className="order-remark">备注：{order.remark}</div>}
      <Flex justify="space-between" align="center" className="order-footer">
        <Text strong>{formatFen(order.payableFen)}</Text>
        <Button type="primary" loading={loading} onClick={() => onAdvance(order, target)}>{action}</Button>
      </Flex>
    </Card>
  )
}

function OrderBoard({ storeId }: { storeId: string }) {
  const queryClient = useQueryClient()
  const ordersQuery = useQuery({
    queryKey: ['active-orders', storeId],
    queryFn: () => listActiveOrders(storeId),
    refetchInterval: 10_000,
  })
  const transition = useMutation({
    mutationFn: ({ order, target }: { order: AdminOrder; target: OrderStatus }) => transitionOrder(order, target),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['active-orders', storeId] })
    },
  })
  const columns: Array<{ status: OrderStatus; title: string; color: string }> = [
    { status: 'PAID', title: '待接单', color: '#fa8c16' },
    { status: 'PREPARING', title: '制作中', color: '#1677ff' },
    { status: 'READY', title: '待取餐', color: '#20a162' },
  ]

  const orders = ordersQuery.data ?? []

  return (
    <div>
      <Flex justify="space-between" align="center" wrap="wrap" gap={12} className="section-heading">
        <div><Title level={3}>实时订单</Title><Text type="secondary">服务端订单 · 每 10 秒自动同步</Text></div>
        <Segmented options={['全部', '堂食', '自取']} defaultValue="全部" />
      </Flex>
      {ordersQuery.error && <Alert type="error" showIcon message={ordersQuery.error.message} className="orders-alert" />}
      {transition.error && <Alert type="error" showIcon message={transition.error.message} className="orders-alert" />}
      {ordersQuery.isLoading && <div className="orders-loading"><Spin size="large" /></div>}
      <Row gutter={[16, 16]}>
        {columns.map((column) => {
          const matching = orders.filter((order) => order.status === column.status)
          return (
            <Col xs={24} lg={8} key={column.status}>
              <div className="kanban-column">
                <Flex justify="space-between" align="center" className="kanban-title">
                  <Space><span className="status-dot" style={{ background: column.color }} /><Text strong>{column.title}</Text></Space>
                  <Badge count={matching.length} showZero color={column.color} />
                </Flex>
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                  {matching.map((order) => <OrderCard key={order.id} order={order} onAdvance={(nextOrder, target) => transition.mutate({ order: nextOrder, target })} loading={transition.isPending && transition.variables?.order.id === order.id} />)}
                  {matching.length === 0 && <div className="empty-column">当前没有订单</div>}
                </Space>
              </div>
            </Col>
          )
        })}
      </Row>
    </div>
  )
}

function Dashboard() {
  return (
    <Space direction="vertical" size={20} style={{ width: '100%' }}>
      <div><Title level={3}>今日经营概览</Title><Text type="secondary">数据更新时间 12:30:25</Text></div>
      <Row gutter={[16, 16]}>
        <Col xs={12} xl={6}><Card><Statistic title="今日营业额" value={3286} precision={2} prefix="¥" /><Text type="success">较昨日 +12.8%</Text></Card></Col>
        <Col xs={12} xl={6}><Card><Statistic title="支付订单" value={86} suffix="单" /><Text type="secondary">其中自取 52 单</Text></Card></Col>
        <Col xs={12} xl={6}><Card><Statistic title="客单价" value={38.21} precision={2} prefix="¥" /><Text type="success">较昨日 +2.3%</Text></Card></Col>
        <Col xs={12} xl={6}><Card><Statistic title="待处理" value={3} suffix="单" valueStyle={{ color: '#df2029' }} /><Text type="warning">最长等待 12 分钟</Text></Card></Col>
      </Row>
      <Row gutter={[16, 16]}>
        <Col xs={24} xl={15}>
          <Card title="时段订单趋势" extra={<Text type="secondary">订单量</Text>}>
            <div className="trend-chart" aria-label="时段订单趋势图">
              {[18, 28, 46, 72, 56, 82, 66, 44].map((height, index) => (
                <div className="trend-bar" key={index} style={{ height: `${height}%` }}><span>{10 + index}:00</span></div>
              ))}
            </div>
          </Card>
        </Col>
        <Col xs={24} xl={9}>
          <Card title="渠道占比">
            <Space direction="vertical" size={18} style={{ width: '100%' }}>
              <div><Flex justify="space-between"><Text>微信小程序</Text><Text>56%</Text></Flex><Progress percent={56} showInfo={false} strokeColor="#20a162" /></div>
              <div><Flex justify="space-between"><Text>抖音小程序</Text><Text>27%</Text></Flex><Progress percent={27} showInfo={false} strokeColor="#161823" /></div>
              <div><Flex justify="space-between"><Text>支付宝小程序</Text><Text>17%</Text></Flex><Progress percent={17} showInfo={false} strokeColor="#1677ff" /></div>
            </Space>
          </Card>
        </Col>
      </Row>
    </Space>
  )
}

function MenuManagement({ storeId }: { storeId: string }) {
  const queryClient = useQueryClient()
  const menuQuery = useQuery({
    queryKey: ['admin-menu', storeId],
    queryFn: () => listAdminMenu(storeId),
  })
  const availability = useMutation({
    mutationFn: ({ item, available }: { item: AdminMenuItem; available: boolean }) => setMenuAvailability(storeId, item, available),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin-menu', storeId] })
    },
  })
  const columns: TableProps<AdminMenuItem>['columns'] = useMemo(() => [
    { title: '菜品', dataIndex: 'name', render: (name: string) => <Text strong>{name}</Text> },
    { title: '分类', dataIndex: 'categoryName' },
    { title: '价格', dataIndex: 'priceFen', render: formatFen },
    { title: '发布', dataIndex: 'published', render: (published: boolean) => <Tag color={published ? 'blue' : 'default'}>{published ? '已发布' : '草稿'}</Tag> },
    { title: '状态', dataIndex: 'available', render: (available: boolean) => <Tag color={available ? 'success' : 'default'}>{available ? '在售' : '售罄'}</Tag> },
    { title: '操作', render: (_, item) => <Space><Button type="link">编辑</Button><Button type="link" danger={item.available} loading={availability.isPending && availability.variables?.item.id === item.id} onClick={() => availability.mutate({ item, available: !item.available })}>{item.available ? '设为售罄' : '恢复在售'}</Button></Space> },
  ], [availability])

  return (
    <Card title="菜单管理" extra={<Space><Button>分类排序</Button><Button type="primary">新增菜品</Button></Space>}>
      {menuQuery.error && <Alert type="error" showIcon message={menuQuery.error.message} className="orders-alert" />}
      {availability.error && <Alert type="error" showIcon message={availability.error.message} className="orders-alert" />}
      <Table rowKey="id" columns={columns} dataSource={menuQuery.data ?? []} loading={menuQuery.isLoading} pagination={false} />
    </Card>
  )
}

function Placeholder({ title }: { title: string }) {
  return <Card><div className="placeholder"><Title level={3}>{title}</Title><Text type="secondary">该模块将在对应实施阶段连接 Rust API。</Text></div></Card>
}

export default function App() {
  const [staff, setStaff] = useState<Staff | null | undefined>(undefined)
  const [active, setActive] = useState('dashboard')
  const [open, setOpen] = useState(true)

  useEffect(() => {
    if (!hasCsrfToken()) {
      setStaff(null)
      return
    }
    getMe().then(setStaff).catch((error: unknown) => {
      if (!(error instanceof AdminApiError) || error.status !== 401) {
        console.error('Admin session restoration failed', error)
      }
      setStaff(null)
    })
  }, [])

  if (staff === undefined) {
    return <main className="app-loading"><Spin size="large" /></main>
  }
  if (staff === null) {
    return <LoginPage onLogin={setStaff} />
  }

  const content = active === 'dashboard' ? <Dashboard />
    : active === 'orders' ? <OrderBoard storeId={staff.storeId} />
      : active === 'menu' ? <MenuManagement storeId={staff.storeId} />
        : <Placeholder title={menuLabels[active] ?? '功能模块'} />

  const signOut = async () => {
    try {
      await logout()
    } finally {
      setStaff(null)
    }
  }

  return (
    <Layout className="app-shell">
      <Sider width={216} breakpoint="lg" collapsedWidth={72} theme="light" className="sidebar">
        <div className="brand"><span className="brand-mark">炒</span><span className="brand-name">家常小炒</span></div>
        <Menu mode="inline" selectedKeys={[active]} items={menuItems} onClick={({ key }) => setActive(key)} />
      </Sider>
      <Layout>
        <Header className="topbar">
          <Flex justify="space-between" align="center" style={{ width: '100%' }}>
            <Space size={18}><Text strong>船山路店</Text><Space size={6}><Switch size="small" checked={open} onChange={setOpen} /><Tag color={open ? 'success' : 'default'}>{open ? '营业中' : '已打烊'}</Tag></Space></Space>
            <Space><Badge dot><Button type="text">通知</Button></Badge><Avatar style={{ background: '#df2029' }}>{staff.displayName.slice(0, 1)}</Avatar><Text>{staff.displayName}</Text><Button type="link" onClick={() => void signOut()}>退出</Button></Space>
          </Flex>
        </Header>
        <Content className="content">{content}</Content>
      </Layout>
    </Layout>
  )
}
